import { useInfiniteQuery } from "@tanstack/react-query";
import debounce from "lodash/debounce";
import { useEffect, useMemo, useRef, useState } from "react";

import { FILTER_ALL_COMPANIES_QUERY_KEY } from "@config/query";
import companyService from "@services/company.service";
import { ICompany } from "@interfaces/company.interface";

function useCompaniesWithoutFilters(searchQuery: string) {
  return useInfiniteQuery({
    queryKey: [FILTER_ALL_COMPANIES_QUERY_KEY, searchQuery],
    queryFn: async ({ pageParam }: { pageParam: any }) => {
      const params = new URLSearchParams();
      if (searchQuery) {
        params.append("search", searchQuery);
        params.append("ordering", "title");
        params.append("offset", String(pageParam.offset ?? 0));
      } else {
        params.append("limit", String(pageParam.limit));
        params.append("offset", String(pageParam.offset ?? 0));
        params.append("ordering", "title");
      }

      return await companyService.getCompanies(params);
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage.next) return undefined;

      const url = new URL(lastPage.next);
      return Object.fromEntries(url.searchParams.entries()) as any;
    },
    select: (data) => {
      return data.pages.flatMap((page) => page.results);
    },
    initialPageParam: {
      limit: 20,
    } as any,
  });
}

export const useCompanyOptions = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [defaultOptions, setDefaultOptions] = useState<ICompany[]>([]);
  const callbackRef = useRef<((arr: Array<ICompany>) => void) | null>(null);

  const {
    data: allCompanies,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useCompaniesWithoutFilters(searchQuery);

  const debouncedSearch = useMemo(
    () =>
      debounce((inputValue: string) => {
        setSearchQuery(inputValue);
      }, 300),
    []
  );

  const searchedOptions = useMemo(() => {
    if (isLoading || !allCompanies) return [];

    return allCompanies.map((company) => ({
      ...company,
      label: company.title,
      value: company.id,
    }));
  }, [allCompanies, isLoading]);

  useEffect(() => {
    if (!searchQuery && allCompanies) {
      setDefaultOptions(
        allCompanies.map((company) => ({
          ...company,
          label: company.title,
          value: company.id,
        }))
      );
    }
  }, [searchQuery, allCompanies]);

  useEffect(() => {
    if (isLoading) return;
    if (callbackRef.current) {
      callbackRef.current(searchedOptions);
      callbackRef.current = null;
    }
  }, [searchedOptions, isLoading]);

  const loadOptions = (_: string, callback: (arr: Array<ICompany>) => void) => {
    callbackRef.current = callback;
  };

  return {
    loadOptions,
    debouncedSearch,
    searchQuery,
    setSearchQuery,
    defaultOptions,
    searchedOptions,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
  };
};
